import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";

import { Tooltip } from "@mui/material";
import {
  DataGrid,
  GridCellParams,
  GridColDef,
  GridColumnHeaderParams,
  GridPaginationModel,
  GridRowParams,
  useGridApiRef,
} from "@mui/x-data-grid";

import { useAuth } from "../../contexts/AuthContext";
import { Job, TemplateSetAggregate } from "../../services/api";
import { UserRole } from "../../services/api/AuthUser";
import { getLocalTime, getRelativeTime } from "../../services/utils/dateTime";
import { getFilename } from "../../services/utils/misc";
import { JobStatus as JobStatusEnum, parseJobStatus } from "../../services/utils/status";
import {
  isValidCreativeID,
  isValidCreativeRenditionID,
  isValidDateRange,
  isValidJobID,
  isValidNetworkID,
  isValidSource,
} from "../../services/utils/validator";
import { CustomGridPagination } from "../shared/Pagination";
import JobRowAction from "./JobRowAction";
import JobStatus from "./JobStatus";
import JobsTableBar from "./JobsTableBar";
import dateRangeFilter from "./filters/DateRangeFilter";
import {
  getCsvFilterQueryString,
  getDateRangeFilterQueryString,
  getFilterQueryString,
  getMultiSelectFilterQueryString,
} from "./filters/FiltersHelper";
import { mapToSelectOptions, multiEnumFilter, multiSelectFilter } from "./filters/SelectFlter";
import { csvTextFilter, regexTextFilter } from "./filters/TextFilter";

const JobsTable: React.FC<{
  jobs: Job[];
  totalRows: number;
  availableSets: TemplateSetAggregate[];
}> = ({ jobs, totalRows, availableSets }) => {
  const location = useLocation();
  const { currentUser } = useAuth();
  const backfill = location.pathname === "/backfill";
  const [searchParams, setSearchParams] = useSearchParams();
  const [paginationModel, setPaginationModel] = useState({ pageSize: 25, page: 0 });
  const [selectedIDs, setSelectedIDs] = useState<string[]>([]);
  const gridApiRef = useGridApiRef();
  const [templateSetsMap, setTemplateSetsMap] = useState<Map<string, string>>(new Map());

  useEffect(() => {
    const paginationModel = {
      pageSize: parseInt(searchParams.get("limit") || "25"),
      // Since DataGrid indexing starts from 0 but api expects 1 changing it here.
      page: parseInt(searchParams.get("page") || "1") - 1,
    };
    setPaginationModel(paginationModel);
  }, [searchParams]);

  useEffect(() => {
    setTemplateSetsMap(
      availableSets.reduce((map, item) => {
        map.set(item.id, item.disabled ? `${item.name} (deleted)` : item.name);
        return map;
      }, new Map<string, string>()),
    );
  }, [availableSets]);

  const csv = (value: string) => value?.replaceAll(/\s+/g, "").split(",");
  const validateJobIDs = (value: string) => csv(value).every(isValidJobID);
  const validateCreativeIDs = (value: string) => csv(value).every(isValidCreativeID);
  const validateCreativeRenditionIDs = (value: string) => csv(value).every(isValidCreativeRenditionID);
  const validateNetworkIDs = (value: string) => csv(value).every(isValidNetworkID);

  const handlePaginationChange = (paginationModel: GridPaginationModel) => {
    setPaginationModel(paginationModel);
    const updatedSearchParams = new URLSearchParams(searchParams);
    updatedSearchParams.set("page", (paginationModel.page + 1).toString());
    updatedSearchParams.set("limit", paginationModel.pageSize.toString());
    setSearchParams(updatedSearchParams);
  };

  const handleRowSelectionModelChange = (newRowSelectionModel: any[]) => {
    const selectedIdsAsString: string[] = newRowSelectionModel.map((id) => String(id));
    setSelectedIDs(selectedIdsAsString);
  };

  // TODO: We should fix the re-render issue for filter sub components.
  /* eslint-disable react-hooks/exhaustive-deps */
  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "_id",
        headerName: "Job ID",
        minWidth: 220,
        align: "center",
        headerAlign: "center",
        flex: 1,
        sortable: false,
        renderHeader: (params: GridColumnHeaderParams) => <strong>{params.colDef.headerName}</strong>,
        renderCell: (params) => <Link to={`/jobs/${params.value}`}>{params.value}</Link>,
        filterOperators: [csvTextFilter(validateJobIDs, getCsvFilterQueryString("jobids"))],
      },
      {
        field: "creative_id",
        headerName: "Creative ID",
        minWidth: 300,
        align: "center",
        headerAlign: "center",
        flex: 1,
        sortable: false,
        renderHeader: (params: GridColumnHeaderParams) => <strong>{params.colDef.headerName}</strong>,
        filterOperators: [csvTextFilter(validateCreativeIDs, getCsvFilterQueryString("creativeids"))],
      },
      {
        field: "source_creative_rendition_id",
        headerName: "Rendition ID",
        minWidth: 110,
        align: "center",
        headerAlign: "center",
        flex: 1,
        sortable: false,
        renderHeader: (params: GridColumnHeaderParams) => <strong>{params.colDef.headerName}</strong>,
        filterOperators: [
          csvTextFilter(validateCreativeRenditionIDs, getCsvFilterQueryString("creativerenditionids")),
        ],
      },
      {
        field: "network_id",
        headerName: "Network ID",
        minWidth: 100,
        align: "center",
        headerAlign: "center",
        flex: 1,
        sortable: false,
        renderHeader: (params: GridColumnHeaderParams) => <strong>{params.colDef.headerName}</strong>,
        filterOperators: [csvTextFilter(validateNetworkIDs, getCsvFilterQueryString("networkids"))],
      },
      {
        field: "set_id",
        headerName: "Set",
        sortable: false,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 150,
        renderHeader: (params: GridColumnHeaderParams) => <strong>{params.colDef.headerName}</strong>,
        renderCell: (params: GridCellParams) => {
          const value = params.value as string;
          const displayValue = templateSetsMap.get(value);
          return value ? `${value} - ${displayValue || ""}` : "";
        },
        filterOperators: [
          multiSelectFilter(
            mapToSelectOptions(templateSetsMap),
            getMultiSelectFilterQueryString("setid"),
            (v, l) => `${v} - ${l}`,
          ),
        ],
      },
      {
        field: "backfill_date",
        headerName: "Backfill Date",
        minWidth: 120,
        flex: 1,
        align: "center",
        headerAlign: "center",
        sortable: false,
        filterable: false,
        renderHeader: (params: GridColumnHeaderParams) => <strong>{params.colDef.headerName}</strong>,
        renderCell: (params: GridCellParams) => {
          const utcTime = params.value as string;
          return (
            <Tooltip title={getLocalTime(utcTime)}>
              <span>{getRelativeTime(utcTime)}</span>
            </Tooltip>
          );
        },
      },
      {
        field: "deactivation_date",
        headerName: "Deactivation Date",
        minWidth: 130,
        flex: 1,
        align: "center",
        headerAlign: "center",
        sortable: false,
        filterable: false,
        renderHeader: (params: GridColumnHeaderParams) => <strong>{params.colDef.headerName}</strong>,
        renderCell: (params: GridCellParams) => {
          const utcTime = params.value as string;
          return (
            <Tooltip title={getLocalTime(utcTime)}>
              <span>{getRelativeTime(utcTime)}</span>
            </Tooltip>
          );
        },
      },
      {
        field: "created_date",
        headerName: "Created Date",
        minWidth: 120,
        flex: 1,
        align: "center",
        headerAlign: "center",
        sortable: false,
        renderHeader: (params: GridColumnHeaderParams) => <strong>{params.colDef.headerName}</strong>,
        renderCell: (params: GridCellParams) => {
          const utcTime = params.value as string;
          return (
            <Tooltip title={getLocalTime(utcTime)}>
              <span>{getRelativeTime(utcTime)}</span>
            </Tooltip>
          );
        },
        filterOperators: [
          dateRangeFilter(
            isValidDateRange,
            getDateRangeFilterQueryString({
              startDate: "start",
              startTime: "starttime",
              endDate: "end",
              endTime: "endtime",
            }),
          ),
        ],
      },
      {
        field: "source",
        headerName: "Source",
        flex: 1,
        minWidth: 150,
        align: "center",
        headerAlign: "center",
        sortable: false,
        renderHeader: (params: GridColumnHeaderParams) => <strong>{params.colDef.headerName}</strong>,
        renderCell: (params: GridCellParams) => getFilename(params.value as string),
        filterOperators: [regexTextFilter(isValidSource, getFilterQueryString("source"))],
      },
      {
        field: "status",
        headerName: "Status",
        minWidth: 150,
        sortable: false,
        flex: 1,
        align: "center",
        headerAlign: "center",
        renderHeader: (params: GridColumnHeaderParams) => <strong>{params.colDef.headerName}</strong>,
        renderCell: (params: GridCellParams) => {
          return <JobStatus status={parseJobStatus(params.value as string)} />;
        },
        filterOperators: [multiEnumFilter(JobStatusEnum, getMultiSelectFilterQueryString("status"))],
      },
      {
        field: "Action",
        headerName: "Action",
        minWidth: 120,
        sortable: false,
        flex: 1,
        filterable: false,
        disableExport: true,
        align: "center",
        headerAlign: "center",
        renderHeader: (params: GridColumnHeaderParams) => <strong>{params.colDef.headerName}</strong>,
        renderCell: (params: GridCellParams) => <JobRowAction row={params.row} />,
      },
    ],
    [templateSetsMap],
  );
  /* eslint-enable react-hooks/exhaustive-deps */

  return (
    <DataGrid
      apiRef={gridApiRef}
      initialState={{
        pagination: {
          paginationModel: { pageSize: 25, page: 1 },
        },
        columns: {
          columnVisibilityModel: {
            backfill_date: backfill,
            deactivation_date: backfill,
            Action: !backfill,
            created_date: !backfill,
            set_id: false,
          },
        },
      }}
      rowCount={totalRows}
      paginationMode="server"
      pageSizeOptions={[25, 50, 75, 100]}
      paginationModel={paginationModel}
      onPaginationModelChange={handlePaginationChange}
      getRowId={(job) => job._id}
      checkboxSelection={!backfill && [UserRole.ADMIN, UserRole.OPERATOR].includes(currentUser.role)}
      isRowSelectable={(params: GridRowParams) => (params.row as Job).status !== JobStatusEnum.INACTIVE}
      disableRowSelectionOnClick
      rows={jobs || []}
      columns={columns}
      slots={{
        toolbar: JobsTableBar,
        pagination: CustomGridPagination,
      }}
      slotProps={{
        filterPanel: {
          sx: {
            "& .MuiDataGrid-filterFormValueInput": {
              width: "auto",
              height: "auto",
            },
          },
          filterFormProps: {
            operatorInputProps: {
              disabled: true,
              sx: { display: "none" },
            },
            deleteIconProps: {
              disabled: true,
              sx: { display: "none" },
            },
          },
        },
        toolbar: {
          ids: selectedIDs,
          backfill: backfill,
        },
      }}
      onRowSelectionModelChange={handleRowSelectionModelChange}
      sx={(theme) => ({
        border: "0",
        ".MuiDataGrid-row.Mui-even": {
          backgroundColor: theme.palette.action.hover,
        },
      })}
      getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? "Mui-even" : "Mui-odd")}
      filterMode="server"
      autoHeight
      {...jobs}
    ></DataGrid>
  );
};

export default JobsTable;
